import { isMarkupRateUnit, storedRateToPercent } from './quoteItemAmounts';

/**
 * 格式化數字輸入欄位
 * 用於將數字格式化為帶千位符號的字串，如果沒有小數點就顯示整數
 */

/**
 * 格式化數字為帶千位符號的字串
 * @param value 數字值
 * @returns 格式化後的字串（例如：1,234 或 1,234.56）
 */
export function formatNumberInput(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) {
    return '';
  }
  
  // 如果沒有小數點，顯示整數
  if (numValue % 1 === 0) {
    return Math.floor(numValue).toLocaleString('zh-TW');
  }
  
  // 有小數點，保留小數並加上千位符號
  return numValue.toLocaleString('zh-TW', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

/**
 * 格式化單價顯示（整數無小數、有千分位；非整數顯示小數）
 * @param value 數字值
 * @returns 格式化後的字串（例如：3,030 或 1,042.5）
 */
export function formatUnitPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  const num = Number(value);
  if (Number.isInteger(num)) {
    return num.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
  }
  return num.toLocaleString('zh-TW', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

/**
 * 格式化單價完整顯示（含 NT$ 或 %，依單位決定）
 * @param value 數字值
 * @param unit 單位（若為 '%' 則不顯示 NT$，僅顯示數字與 %）
 * @returns 格式化後的字串（例如：NT$ 3,030 或 5%）
 */
export function formatUnitPriceDisplay(value: number | null | undefined, unit?: string): string {
  if (isMarkupRateUnit(unit)) {
    const percent = storedRateToPercent(value ?? 0, unit);
    return `${formatUnitPrice(percent)}%`;
  }
  const formatted = formatUnitPrice(value);
  return `NT$ ${formatted}`;
}

/**
 * 格式化郵件金額（四捨五入整數 + 千分位，供郵件模板使用）
 * @param value 數字值
 * @returns 格式化後的字串（例如：123,456）
 */
export function formatEmailAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '0';
  const rounded = Math.round(Number(value));
  return rounded.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
}

/**
 * 解析格式化字串回數字
 * @param value 格式化後的字串（例如：1,234 或 1,234.56）
 * @returns 數字值
 */
export function parseNumberInput(value: string): number {
  if (!value || value.trim() === '') {
    return 0;
  }
  
  // 移除千位符號
  const cleaned = value.replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
}

